import { useCallback, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import FormComp from "../../Componentes/FormComp";
import Overlay from "../../Componentes/Overlay";
import Formulario from "../../Componentes/Utils/Formulario";
import Modal from "../../Componentes/Utils/Modal";
import { useModal } from "../../contexts/ModalContext";
import { useToast } from "../../contexts/ToastContext";
import useApi from "../../hooks/useApi";

export const MODAL_ID = "gestaoAcessosV2NovoGrupo";
const LIST_MODULOS_URL = "/Painel/API/AccessManagement/GestaoAcessos/ListModulos/";
const CREATE_URL = "/Painel/API/AccessManagement/GestaoAcessos/CreateGrupo/";

const ModalTitle = styled.h3`
    margin: 0 0 18px 0;
    padding: 0 0 12px 2px;
    border-bottom: 1px solid #e5e7eb;
    font-size: 15px;
    font-weight: 700;
    color: #263b4b;
    letter-spacing: 0.02em;
    text-align: left;
    width: 100%;
    box-sizing: border-box;
`;

const BotoesWrapper = styled.div`
    margin-top: 20px;
    display: flex;
    justify-content: flex-end;
    gap: 10px;
`;

const Botao = styled.button`
    padding: 9px 14px;
    background-color: ${(p) => (p.$cancel ? "#e5e7eb" : "#2563eb")};
    color: ${(p) => (p.$cancel ? "#333" : "#fff")};
    border: none;
    border-radius: 5px;
    cursor: pointer;
    font-weight: 600;
    font-size: 13px;
    transition: 0.2s all ease;

    &:disabled {
        background-color: ${(p) => (p.$cancel ? "#e5e7eb" : "#93c5fd")};
        cursor: not-allowed;
    }

    &:hover:not(:disabled) {
        background-color: ${(p) => (p.$cancel ? "#d1d5db" : "#1d4ed8")};
    }
`;

const StatusMsg = styled.p`
    margin: 8px 0 0 0;
    font-size: 13px;
    color: ${(p) => (p.$error ? "#b91c1c" : "#64748b")};
`;

const ModalNovoGrupo = ({ onSaved }) => {
    const api = useApi();
    const { closeModal } = useModal();
    const { notifyError, notifySuccess } = useToast();

    const [loadingModulos, setLoadingModulos] = useState(true);
    const [modulos, setModulos] = useState([]);
    const [nome, setNome] = useState("");
    const [descricao, setDescricao] = useState("");
    const [modulo, setModulo] = useState("");
    const [saving, setSaving] = useState(false);

    const fetchModulos = useCallback(async () => {
        setLoadingModulos(true);

        try {
            const { data } = await api.get(LIST_MODULOS_URL);
            const list = Array.isArray(data) ? data : [];
            setModulos(list);
            setModulo(list.length === 1 ? list[0] : "");
        } catch (error) {
            console.error(error);
            setModulos([]);
            notifyError(error?.response?.data?.message || "Erro ao carregar módulos.");
        } finally {
            setLoadingModulos(false);
        }
    }, [api, notifyError]);

    useEffect(() => {
        fetchModulos();
    }, [fetchModulos]);

    const canSave = useMemo(
        () => nome.trim() !== "" && modulo.trim() !== "" && !saving && !loadingModulos,
        [loadingModulos, modulo, nome, saving]
    );

    const handleClose = () => {
        closeModal(MODAL_ID);
    };

    const handleSave = async (e) => {
        e?.preventDefault?.();
        if (!canSave) return;

        setSaving(true);

        try {
            const { data } = await api.post(CREATE_URL, {
                nome: nome.trim(),
                descricao: descricao.trim(),
                modulo: modulo.trim(),
            });

            notifySuccess(data?.message || "Grupo criado com sucesso.");
            await onSaved?.(data);
            handleClose();
        } catch (error) {
            console.error(error);
            notifyError(error?.response?.data?.message || "Erro ao criar grupo.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Overlay state changeContent={handleClose}>
            <Modal header close={handleClose} size="md">
                <ModalTitle>NOVO GRUPO</ModalTitle>

                <Formulario onSubmit={handleSave}>
                    <FormComp
                        title="NOME"
                        type="text"
                        placeholder="Ex: RH_GESTOR"
                        valor={nome}
                        changeFunc={(e) => setNome(e.target.value)}
                        required
                    />
                    <FormComp
                        title="DESCRIÇÃO"
                        type="text"
                        placeholder="Opcional"
                        valor={descricao}
                        changeFunc={(e) => setDescricao(e.target.value)}
                    />
                    {loadingModulos ? (
                        <StatusMsg>Carregando módulos...</StatusMsg>
                    ) : modulos.length === 0 ? (
                        <StatusMsg $error>Você não tem permissão para criar grupos.</StatusMsg>
                    ) : modulos.length === 1 ? (
                        <FormComp title="MÓDULO" type="text" valor={modulos[0]} readOnly />
                    ) : (
                        <FormComp
                            title="MÓDULO"
                            type="select"
                            options={modulos}
                            valor={modulo}
                            changeFunc={(e) => setModulo(e.target.value)}
                            required
                        />
                    )}

                    <BotoesWrapper>
                        <Botao $cancel type="button" onClick={handleClose} disabled={saving}>
                            CANCELAR
                        </Botao>
                        <Botao type="submit" disabled={!canSave}>
                            {saving ? "SALVANDO…" : "SALVAR"}
                        </Botao>
                    </BotoesWrapper>
                </Formulario>
            </Modal>
        </Overlay>
    );
};

export default ModalNovoGrupo;
